import { useCallback, useEffect, useState } from 'react';
import { ApiError, apiFetch } from '../../../lib/api';
import { agente } from '../../../lib/agentes';
import { Modal } from '../../../components/Modal';
import { Spinner } from '../../../components/Spinner';

/**
 * Observabilidad — telemetría real de los cerebros del equipo por agente × modelo:
 * corridas, éxito, tokens (incluido pensamiento), reparaciones, costo y latencia.
 * Ventana temporal seleccionable y limpieza de la bitácora (solo telemetría:
 * no toca ediciones, items ni versiones de conocimiento).
 */

interface Fila {
  agent_code: string;
  model: string;
  runs: number;
  success_rate: number;
  tokens_in: number;
  tokens_out: number;
  tokens_reasoning: number;
  repairs: number;
  est_cost: number;
  avg_latency_ms: number;
}

const nf = new Intl.NumberFormat('es-CL');
const usd = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });

const VENTANAS = [
  { dias: 1, label: '24 horas' },
  { dias: 7, label: '7 días' },
  { dias: 30, label: '30 días' },
  { dias: 0, label: 'Todo' },
] as const;

function traducirError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'Ocurrió un error inesperado. Inténtalo de nuevo.';
}

export default function SeccionObservabilidad() {
  const [filas, setFilas] = useState<Fila[]>([]);
  const [dias, setDias] = useState<number>(30);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmarLimpieza, setConfirmarLimpieza] = useState(false);
  const [alcance, setAlcance] = useState<'todo' | 'antiguos'>('antiguos');
  const [limpiando, setLimpiando] = useState(false);
  const [avisoLimpieza, setAvisoLimpieza] = useState<string | null>(null);

  const cargar = useCallback(async (ventana: number) => {
    setCargando(true);
    setError(null);
    try {
      const data = await apiFetch<{ summary: Fila[] }>(`/api/admin/agent-runs?dias=${ventana}`);
      setFilas(data.summary ?? []);
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar(dias);
  }, [cargar, dias]);

  async function limpiar() {
    setLimpiando(true);
    setError(null);
    try {
      const qs = alcance === 'antiguos' ? '?antes_de_dias=30' : '';
      const res = await apiFetch<{ eliminados: number }>(`/api/admin/agent-runs${qs}`, { method: 'DELETE' });
      setAvisoLimpieza(`Bitácora limpiada: ${nf.format(res.eliminados)} registro${res.eliminados === 1 ? '' : 's'} eliminado${res.eliminados === 1 ? '' : 's'}.`);
      setConfirmarLimpieza(false);
      await cargar(dias);
    } catch (err) {
      setError(traducirError(err));
      setConfirmarLimpieza(false);
    } finally {
      setLimpiando(false);
    }
  }

  const totales = filas.reduce(
    (a, f) => ({
      runs: a.runs + f.runs,
      tokens: a.tokens + f.tokens_in + f.tokens_out,
      costo: a.costo + f.est_cost,
      repairs: a.repairs + f.repairs,
    }),
    { runs: 0, tokens: 0, costo: 0, repairs: 0 },
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-normal text-gris">
          Telemetría de los cerebros del equipo por agente y modelo: corridas, éxito, tokens y costo.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div role="tablist" aria-label="Ventana temporal" className="flex rounded-full bg-slate-100 p-0.5">
            {VENTANAS.map((v) => (
              <button
                key={v.dias}
                role="tab"
                type="button"
                aria-selected={dias === v.dias}
                onClick={() => setDias(v.dias)}
                className={`pill !h-7 ${dias === v.dias ? 'bg-white text-azul-profundo shadow-sm' : 'text-gris hover:text-azul-noche'}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { setAvisoLimpieza(null); setConfirmarLimpieza(true); }}
            disabled={cargando || filas.length === 0}
            className="inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-medium text-red-600 transition-colors duration-150 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            </svg>
            Limpiar bitácora
          </button>
        </div>
      </div>

      {avisoLimpieza && (
        <p role="status" className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-normal text-emerald-800 ring-1 ring-inset ring-emerald-200">
          {avisoLimpieza}
        </p>
      )}
      {error && <p role="alert" className="alerta-error">{error}</p>}

      {cargando ? (
        <div role="status" aria-live="polite" className="tarjeta flex items-center justify-center px-6 py-16">
          <Spinner tamano="md" />
        </div>
      ) : filas.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-16 text-center">
          <p className="text-sm font-medium text-azul-profundo">Sin corridas en esta ventana</p>
          <p className="mt-3 text-sm font-normal text-gris">
            Aquí aparece cada llamada LLM del equipo: corridas de ediciones y pruebas del banco de entrenamiento.
          </p>
        </div>
      ) : (
        <>
          {/* Totales de la ventana */}
          <div className="flex flex-wrap gap-2">
            <span className="chip bg-slate-100 text-slate-700 ring-slate-300">{nf.format(totales.runs)} llamadas</span>
            <span className="chip bg-slate-100 text-slate-700 ring-slate-300">{nf.format(totales.tokens)} tokens</span>
            <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">{usd.format(totales.costo)} costo estimado</span>
            {totales.repairs > 0 && (
              <span className="chip bg-amber-50 text-amber-700 ring-amber-300">{nf.format(totales.repairs)} reparaciones</span>
            )}
          </div>

          <div className="tarjeta overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-slate-300 bg-[#F8FAFC]">
                    <th scope="col" className="th px-5">Agente</th>
                    <th scope="col" className="th px-5">Modelo</th>
                    <th scope="col" className="th px-5 text-right">Corridas</th>
                    <th scope="col" className="th px-5 text-right">Éxito</th>
                    <th scope="col" className="th px-5 text-right">Tokens in / out</th>
                    <th scope="col" className="th px-5 text-right">Reparaciones</th>
                    <th scope="col" className="th px-5 text-right">Costo est.</th>
                    <th scope="col" className="th px-5 text-right">Latencia media</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filas.map((r, i) => {
                    const id = agente(r.agent_code);
                    return (
                      <tr key={`${r.agent_code}-${r.model}-${i}`} className="text-sm transition-colors duration-150 hover:bg-celeste/[0.08]">
                        <td className="px-5 py-3">
                          <span className="flex items-center gap-2.5">
                            <span aria-hidden="true" className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[10px] font-semibold ${id.tinte}`}>
                              {r.agent_code}
                            </span>
                            <span className="min-w-0">
                              <span className="block text-[13px] font-medium leading-tight text-azul-noche">{id.nombre}</span>
                              <span className="block text-[11px] font-normal leading-tight text-gris">{id.oficio}</span>
                            </span>
                          </span>
                        </td>
                        <td className="px-5 py-3 font-mono text-xs font-normal text-gris">{r.model}</td>
                        <td className="px-5 py-3 text-right font-medium tabular-nums text-texto">{nf.format(r.runs)}</td>
                        <td className="px-5 py-3 text-right tabular-nums">
                          <span className={r.success_rate >= 0.9 ? 'text-emerald-600' : r.success_rate >= 0.7 ? 'text-amber-600' : 'text-red-600'}>
                            {Math.round(r.success_rate * 100)}%
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right font-normal tabular-nums text-gris">
                          {nf.format(r.tokens_in)} / {nf.format(r.tokens_out)}
                          {r.tokens_reasoning > 0 && (
                            <span className="block text-[11px] text-gris">+{nf.format(r.tokens_reasoning)} pensamiento</span>
                          )}
                        </td>
                        <td className={`px-5 py-3 text-right tabular-nums ${r.repairs > 0 ? 'text-amber-700' : 'font-normal text-gris'}`}>
                          {nf.format(r.repairs)}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-texto">{usd.format(r.est_cost)}</td>
                        <td className="px-5 py-3 text-right font-normal tabular-nums text-gris">{nf.format(r.avg_latency_ms)} ms</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Confirmación de limpieza de bitácora */}
      {confirmarLimpieza && (
        <Modal titulo="Limpiar bitácora de corridas" onCerrar={() => setConfirmarLimpieza(false)}>
          <p className="text-sm font-normal leading-relaxed text-texto">
            Esto elimina registros de telemetría LLM (tokens, costos, latencias). No toca
            ediciones, items ni versiones de conocimiento — pero la auditoría "qué pensó cada
            agente" de las corridas antiguas se pierde.
          </p>
          <fieldset className="mt-4 flex flex-col gap-2">
            <legend className="etiqueta">Qué eliminar</legend>
            <label className="flex items-center gap-2.5 text-sm font-normal text-texto">
              <input
                type="radio"
                name="alcance"
                checked={alcance === 'antiguos'}
                onChange={() => setAlcance('antiguos')}
                className="h-4 w-4 accent-azul-profundo"
              />
              Solo registros con más de 30 días
            </label>
            <label className="flex items-center gap-2.5 text-sm font-normal text-texto">
              <input
                type="radio"
                name="alcance"
                checked={alcance === 'todo'}
                onChange={() => setAlcance('todo')}
                className="h-4 w-4 accent-azul-profundo"
              />
              Toda la bitácora (partir de cero)
            </label>
          </fieldset>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirmarLimpieza(false)} disabled={limpiando} className="btn-fantasma">
              Cancelar
            </button>
            <button type="button" onClick={() => void limpiar()} disabled={limpiando} className="btn-peligro">
              {limpiando ? 'Limpiando…' : alcance === 'todo' ? 'Eliminar todo' : 'Eliminar antiguos'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
